import { useClientTranslation } from '@/lib/core/i18n';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { IApiContentResponseModel } from '@/services/platform/api/model';

/** Sentinel value for the "All hubs" option (Radix Select disallows empty item values). */
const ALL_HUBS = 'all';

export interface HubDropdownProps {
  hubs: IApiContentResponseModel[];
  /** Selected hub key (`hubKey`), or undefined for all hubs. */
  value?: string;
  onChange: (hubKey?: string) => void;
}

/**
 * "All hubs" dropdown fed by the CMS `hubPicker` list. Selecting a hub sets the
 * `hubKey` filter; each hub's `id` is the location node key the events API expects.
 */
export function HubDropdown({ hubs, value, onChange }: HubDropdownProps) {
  const t = useClientTranslation();
  const selected = value ?? ALL_HUBS;
  // The selected item is filtered out of the open list below, so Radix can no
  // longer derive the trigger label from a rendered SelectItem — pass it explicitly.
  const selectedLabel = value
    ? (hubs.find(hub => hub.id === value)?.name ?? t('App.Events.AllHubs'))
    : t('App.Events.AllHubs');

  return (
    <Select value={selected} onValueChange={next => onChange(next === ALL_HUBS ? undefined : next)}>
      <SelectTrigger
        className="text-jncquoi-neutral-1000 h-10 w-[180px] border-0 px-2 text-[12px] font-semibold"
        aria-label={t('App.Events.AllHubs')}
      >
        <SelectValue placeholder={t('App.Events.AllHubs')}>{selectedLabel}</SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-jncquoi-neutral-50 w-45 rounded-none border-0 p-0 py-2">
        {hubs
          .filter(hub => hub.id !== selected)
          .map(hub => (
            <SelectItem
              key={hub.id}
              value={hub.id}
              className="border-0 p-2 text-xs font-semibold tracking-wide not-last:mb-2"
            >
              {hub.name}
            </SelectItem>
          ))}
        {selected !== ALL_HUBS && (
          <SelectItem value={ALL_HUBS} className="border-0 p-2 text-xs font-semibold tracking-wide">
            {t('App.Events.AllHubs')}
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}
